import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  AI_SUGGESTED_TAG_LABEL,
  BRAND_FIELD_DEFINITIONS,
  BRAND_SUGGESTION_BASIS_TEXT,
  VALUE_PHILOSOPHY_OPTIONS,
  type BrandFieldKey,
  type BrandSuggestion,
} from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { PriceTier } from '../../brand/fields/PriceTier';
import { wizardFieldId } from '../draft/brandDraft';
import type { WizardFieldRef } from '../steps';
import { GUIDELINES_FIELD_LABEL } from '../wizardCopy';
import { useSuggestions } from './SuggestionsProvider';
import { useFieldSuggestion } from './useFieldSuggestion';

/** The box's words (spec:123-124). Each button's name also says which field it is for. */
export const SUGGESTION_BOX_COPY = {
  use: 'Use suggestion',
  dismiss: 'Dismiss',
  useFor: (label: string) => `Use suggestion for ${label}`,
  dismissFor: (label: string) => `Dismiss suggestion for ${label}`,
} as const;

/** The field's name as the wizard shows it (the guidelines table is labelled "Writing guidelines"). */
const fieldLabel = (key: BrandFieldKey): string =>
  key === 'writingGuidelines' ? GUIDELINES_FIELD_LABEL : BRAND_FIELD_DEFINITIONS[key].label;

/** The field's own control (a text box, a list's add box, or Value philosophy's chosen card) takes the focus. */
function focusFieldControl(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  const target = el.matches('input, textarea, select, button') ? el : el.querySelector<HTMLElement>('[tabindex="0"]');
  target?.focus();
}

/**
 * The "AI suggested" box under a field (spec:121-125), every wizard field's footer: a dashed box headed "AI
 * suggested", the note naming the suggestion's basis, the suggested text (a short list for a tag list or
 * the guidelines; one of the four options, with its price tier, for Value philosophy), then Use suggestion
 * (filled) and Dismiss (plain) (spec:99, :563). Nothing while no suggestion is on offer for the field.
 *
 * Mounted, it puts its field on the page for SuggestionsProvider, which asks for it while it is empty.
 * Either button takes the box away, so the focus goes to the field it was for.
 */
export function SuggestionSlot({ field }: { field: WizardFieldRef }) {
  const suggestions = useSuggestions();
  const { suggestion } = useFieldSuggestion(field);
  const key = field.kind === 'field' ? field.key : null;
  const register = suggestions?.register;
  const [refocus, setRefocus] = useState(false);

  useEffect(() => (register && key ? register(key) : undefined), [register, key]);

  useEffect(() => {
    if (!refocus || !key) return;
    setRefocus(false);
    focusFieldControl(wizardFieldId(key));
  }, [refocus, key]);

  if (!suggestions || !key || !suggestion) return null;
  return (
    <SuggestionBox
      id={wizardFieldId(key)}
      label={fieldLabel(key)}
      suggestion={suggestion}
      onUse={() => {
        suggestions.use(key);
        setRefocus(true);
      }}
      onDismiss={() => {
        suggestions.dismiss(key);
        setRefocus(true);
      }}
    />
  );
}

function SuggestionBox({
  id,
  label,
  suggestion,
  onUse,
  onDismiss,
}: {
  /** The field's control id. */
  id: string;
  label: string;
  suggestion: BrandSuggestion;
  onUse: () => void;
  onDismiss: () => void;
}) {
  const headingId = `${id}-suggestion`;
  return (
    <div
      role="group"
      aria-labelledby={headingId}
      className="mt-3 rounded-xl border border-dashed border-ai/60 bg-ai-tint/50 px-4 py-3"
    >
      <p id={headingId} className="flex items-center gap-1.5 text-sm font-medium text-ai">
        <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
        {AI_SUGGESTED_TAG_LABEL}
        <span className="sr-only">: {label}</span>
      </p>
      <p className="mt-0.5 text-xs text-ink-muted">{BRAND_SUGGESTION_BASIS_TEXT[suggestion.basis]}</p>
      <div className="mt-2 text-sm text-ink-strong">
        <SuggestedValue suggestion={suggestion} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" aria-label={SUGGESTION_BOX_COPY.useFor(label)} onClick={onUse}>
          {SUGGESTION_BOX_COPY.use}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="plain"
          aria-label={SUGGESTION_BOX_COPY.dismissFor(label)}
          onClick={onDismiss}
        >
          {SUGGESTION_BOX_COPY.dismiss}
        </Button>
      </div>
    </div>
  );
}

/** The suggested value as the field would hold it. */
function SuggestedValue({ suggestion }: { suggestion: BrandSuggestion }) {
  if (suggestion.key === 'valuePhilosophy') {
    const option = VALUE_PHILOSOPHY_OPTIONS.find((o) => o.value === suggestion.value);
    if (!option) return null;
    return (
      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <PriceTier tier={option.tier} />
        <span className="font-medium">{option.label}</span>
        <span className="text-ink-muted">— {option.description}</span>
      </p>
    );
  }
  const { value } = suggestion;
  if (Array.isArray(value)) {
    if (suggestion.key === 'writingGuidelines') {
      return (
        <ul className="list-disc space-y-1 pl-5">
          {value.map((item, i) => (
            <li key={`${i}:${item}`} className="break-words">
              {item}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <ul className="flex flex-wrap gap-1.5">
        {value.map((item, i) => (
          <li key={`${i}:${item}`}>
            <span className="inline-flex items-center rounded-full bg-card px-2 py-0.5 text-xs text-ink-label">{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="whitespace-pre-wrap break-words">{value}</p>;
}
